// Rendu d'un champ du formulaire dynamique selon son type (quatorze types du schéma partagé) :
// de vrais éléments de formulaire avec libellé, aide et erreur ; cibles tactiles de 44 px.
import type { Champ } from '@cc/contrats';
import { Bouton, Champ as ChampSaisie, DateChamp, Selection, ZoneTexte } from '@cc/ui';
import { CircleAlert, LocateFixed } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ChampTableau } from './ChampTableau';
import type { ErreurSaisie } from './formulaire-depuis-fiche';
import styles from './depot.module.css';

export interface PieceDisponible {
  id: string;
  titre: string;
  type: string;
}

export interface ProprietesChampDynamique {
  id: string;
  champ: Champ;
  valeur: unknown;
  onChange: (v: unknown) => void;
  erreur: string | null;
  /** Erreurs des sous-éléments (adresse.voie, lignes.0.col), sans le code du champ. */
  erreursSous: ErreurSaisie[];
  /** Pièces du dossier utilisables par un champ fichier. */
  pieces?: PieceDisponible[];
  /** Valeur calculée par le moteur pour un champ calculé. */
  valeurCalculee?: number | null;
  desactive?: boolean;
}

function Groupe({ id, champ, erreur, children, description }: { id: string; champ: Champ; erreur: string | null; children: ReactNode; description?: string }) {
  return (
    <fieldset className={styles.groupe} id={id} aria-describedby={[champ.aide ? `${id}-aide` : null, erreur ? `${id}-erreur` : null].filter(Boolean).join(' ') || undefined}>
      <legend>
        {champ.libelle}
        {champ.obligatoire ? (
          <span className={styles.obligatoire} aria-hidden="true">
            *
          </span>
        ) : (
          <span className={styles.facultatif}>(facultatif)</span>
        )}
      </legend>
      {champ.aide || description ? (
        <p id={`${id}-aide`} className={styles.aide}>
          {champ.aide ?? description}
        </p>
      ) : null}
      {children}
      {erreur ? (
        <span id={`${id}-erreur`} className={styles.erreur}>
          <CircleAlert aria-hidden strokeWidth={1.75} />
          {erreur}
        </span>
      ) : null}
    </fieldset>
  );
}

function Localisation({ id, champ, valeur, onChange, erreur, erreursSous, desactive }: ProprietesChampDynamique) {
  const v = (valeur ?? {}) as { latitude?: number; longitude?: number };
  const [etat, setEtat] = useState<string | null>(null);
  const localiser = () => {
    if (!navigator.geolocation) {
      setEtat('La localisation n\'est pas disponible sur cet appareil : saisissez les coordonnées.');
      return;
    }
    setEtat('Recherche de la position…');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        onChange({ latitude: Math.round(p.coords.latitude * 1e6) / 1e6, longitude: Math.round(p.coords.longitude * 1e6) / 1e6 });
        setEtat('Position relevée.');
      },
      () => setEtat('Position refusée ou indisponible : saisissez les coordonnées.'),
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  };
  const nombre = (cle: 'latitude' | 'longitude', texte: string) => {
    const suivant = { ...v, [cle]: texte === '' ? undefined : Number(texte) };
    onChange(suivant.latitude === undefined && suivant.longitude === undefined ? undefined : suivant);
  };
  return (
    <Groupe id={id} champ={champ} erreur={erreur} description="Position du lieu concerné (latitude et longitude, ou votre position actuelle)">
      <div className={styles.sousChamps}>
        <ChampSaisie id={`${id}-latitude`} libelle="Latitude" type="number" step="any" inputMode="decimal" value={v.latitude ?? ''} onChange={(e) => nombre('latitude', e.target.value)} erreur={erreursSous.find((e) => e.champ === 'latitude')?.message ?? null} disabled={desactive} />
        <ChampSaisie id={`${id}-longitude`} libelle="Longitude" type="number" step="any" inputMode="decimal" value={v.longitude ?? ''} onChange={(e) => nombre('longitude', e.target.value)} erreur={erreursSous.find((e) => e.champ === 'longitude')?.message ?? null} disabled={desactive} />
      </div>
      <div>
        <Bouton variante="secondaire" petit icone={LocateFixed} onClick={localiser} disabled={desactive}>
          Utiliser ma position
        </Bouton>
      </div>
      {etat ? (
        <p className={styles.aide} role="status">
          {etat}
        </p>
      ) : null}
    </Groupe>
  );
}

export function ChampDynamique(p: ProprietesChampDynamique) {
  const { id, champ, valeur, onChange, erreur, erreursSous, pieces = [], valeurCalculee, desactive } = p;
  const commun = { id, libelle: champ.libelle, aide: champ.aide, erreur, required: champ.obligatoire, facultatif: !champ.obligatoire, disabled: desactive };
  if (champ.calcul) {
    return (
      <div className={styles.groupe}>
        <span className={`${styles.groupe} ${styles.calcule}`} id={id} role="status" aria-live="polite">
          <strong>{champ.libelle}</strong>
          <span>
            {typeof valeurCalculee === 'number' ? `${valeurCalculee.toLocaleString('fr-FR')}${champ.type === 'montant' ? ' francs CFA' : ''}` : 'Calculé une fois les valeurs saisies'}
          </span>
          {champ.aide ? <span className={styles.aide}>{champ.aide}</span> : null}
        </span>
      </div>
    );
  }
  switch (champ.type) {
    case 'texte_long':
      return <ZoneTexte {...commun} rows={4} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChange(e.target.value)} maxLength={champ.validation?.longueur_max ?? 5000} />;
    case 'nombre':
    case 'montant':
      return (
        <ChampSaisie
          {...commun}
          type="number"
          inputMode={champ.type === 'montant' ? 'numeric' : 'decimal'}
          step={champ.type === 'montant' ? 1 : 'any'}
          min={champ.type === 'montant' ? 0 : champ.validation?.min}
          max={champ.validation?.max}
          aide={champ.aide ?? (champ.type === 'montant' ? 'En francs CFA, sans décimale' : undefined)}
          value={valeur === undefined || valeur === null ? '' : String(valeur)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number.isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value))}
        />
      );
    case 'date':
      return <DateChamp {...commun} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChange(e.target.value || undefined)} min={champ.validation?.date_min} max={champ.validation?.date_max} />;
    case 'telephone':
      return <ChampSaisie {...commun} type="tel" autoComplete="tel" value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChange(e.target.value)} />;
    case 'courriel':
      return <ChampSaisie {...commun} type="email" autoComplete="email" value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChange(e.target.value)} />;
    case 'choix_unique': {
      const options = champ.options ?? [];
      if (options.length > 6) {
        return <Selection {...commun} optionVide="Choisir" options={options.map((o) => ({ valeur: o.valeur, libelle: o.libelle }))} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChange(e.target.value || undefined)} />;
      }
      return (
        <Groupe id={id} champ={champ} erreur={erreur}>
          <div className={styles.options} role="radiogroup" aria-label={champ.libelle}>
            {options.map((o) => (
              <label key={o.valeur} className={styles.option}>
                <input type="radio" name={id} value={o.valeur} checked={valeur === o.valeur} disabled={desactive} onChange={() => onChange(o.valeur)} aria-invalid={erreur ? true : undefined} />
                <span>{o.libelle}</span>
              </label>
            ))}
          </div>
        </Groupe>
      );
    }
    case 'choix_multiple': {
      const choisis = Array.isArray(valeur) ? (valeur as string[]) : [];
      return (
        <Groupe id={id} champ={champ} erreur={erreur}>
          <div className={styles.options}>
            {(champ.options ?? []).map((o) => (
              <label key={o.valeur} className={styles.option}>
                <input
                  type="checkbox"
                  value={o.valeur}
                  checked={choisis.includes(o.valeur)}
                  disabled={desactive}
                  onChange={(e) => {
                    const suivants = e.target.checked ? [...choisis, o.valeur] : choisis.filter((x) => x !== o.valeur);
                    onChange(suivants.length > 0 ? suivants : undefined);
                  }}
                />
                <span>{o.libelle}</span>
              </label>
            ))}
          </div>
        </Groupe>
      );
    }
    case 'oui_non':
      return (
        <Groupe id={id} champ={champ} erreur={erreur}>
          <div className={styles.options} role="radiogroup" aria-label={champ.libelle}>
            {[
              { v: true, l: 'Oui' },
              { v: false, l: 'Non' },
            ].map((o) => (
              <label key={o.l} className={styles.option}>
                <input type="radio" name={id} checked={valeur === o.v} disabled={desactive} onChange={() => onChange(o.v)} aria-invalid={erreur ? true : undefined} />
                <span>{o.l}</span>
              </label>
            ))}
          </div>
        </Groupe>
      );
    case 'adresse': {
      const a = (valeur ?? {}) as Record<string, string | undefined>;
      const maj = (cle: string, texte: string) => {
        const suivant = { ...a, [cle]: texte || undefined };
        onChange(Object.values(suivant).some((x) => x) ? suivant : undefined);
      };
      const sous = (cle: string) => erreursSous.find((e) => e.champ === cle)?.message ?? null;
      return (
        <Groupe id={id} champ={champ} erreur={erreur}>
          <ChampSaisie id={`${id}-voie`} libelle="Voie, numéro, lieu-dit" autoComplete="street-address" value={a.voie ?? ''} onChange={(e) => maj('voie', e.target.value)} erreur={sous('voie')} required={champ.obligatoire} disabled={desactive} />
          <div className={styles.sousChamps}>
            <ChampSaisie id={`${id}-quartier`} libelle="Quartier" value={a.quartier ?? ''} onChange={(e) => maj('quartier', e.target.value)} erreur={sous('quartier')} facultatif disabled={desactive} />
            <ChampSaisie id={`${id}-ville`} libelle="Ville" autoComplete="address-level2" value={a.ville ?? ''} onChange={(e) => maj('ville', e.target.value)} erreur={sous('ville')} facultatif disabled={desactive} />
          </div>
          <ChampSaisie id={`${id}-complement`} libelle="Complément" value={a.complement ?? ''} onChange={(e) => maj('complement', e.target.value)} erreur={sous('complement')} facultatif disabled={desactive} />
        </Groupe>
      );
    }
    case 'localisation':
      return <Localisation {...p} />;
    case 'fichier': {
      const utilisables = pieces.filter((x) => !champ.type_piece || x.type === champ.type_piece);
      return (
        <Selection
          {...commun}
          optionVide={utilisables.length === 0 ? 'Aucune pièce jointe pour l\'instant' : 'Choisir une pièce du dossier'}
          options={utilisables.map((x) => ({ valeur: x.id, libelle: x.titre }))}
          value={typeof valeur === 'string' ? valeur : ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          aide={champ.aide ?? 'Les pièces se joignent à l\'étape « Pièces » ; revenez ensuite choisir celle qui correspond.'}
        />
      );
    }
    case 'tableau':
      return <ChampTableau id={id} champ={champ} valeur={valeur} onChange={(v) => onChange(v && v.length > 0 ? v : undefined)} erreur={erreur} erreursSous={erreursSous} desactive={desactive} />;
    default:
      return <ChampSaisie {...commun} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChange(e.target.value)} maxLength={champ.validation?.longueur_max ?? 255} />;
  }
}
